import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import DisputeDialog from "./DisputeDialog";
import { AlertTriangle, MessageSquare, Gavel, CheckCircle2, Clock } from "lucide-react";

const TYPE_LABELS = {
  damaged: "Marchandise abîmée",
  missing: "Marchandise manquante",
  late: "Retard important",
  payment: "Différend de paiement",
  other: "Autre",
};

const STATUS_LABELS = {
  open: { label: "OUVERT", color: "bg-amber-500 text-white", icon: AlertTriangle },
  responded: { label: "RÉPONDU — ATTENTE ADMIN", color: "bg-blue-600 text-white", icon: Gavel },
  resolved: { label: "TRANCHÉ", color: "bg-emerald-600 text-white", icon: CheckCircle2 },
  closed: { label: "CLÔTURÉ", color: "bg-neutral-500 text-white", icon: CheckCircle2 },
};

const DECISION_LABELS = {
  refund_full: "Remboursement intégral à l'expéditeur",
  refund_partial: "Remboursement partiel",
  validate_transporter: "Pas de problème — paiement libéré au transporteur",
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function MissionDisputePanel({ mission, onChange }) {
  const { user } = useAuth();
  const [data, setData] = useState({ dispute: null, can_open: false });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("open");

  const load = async () => {
    if (!mission?.id) return;
    try {
      const { data: d } = await api.get(`/missions/${mission.id}/dispute`);
      setData(d);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  };

  // Recharger uniquement lorsque l'identité ou l'état de la mission change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [mission?.id, mission?.status]);

  if (loading) return null;
  const { dispute, can_open } = data;

  // Le bouton d'ouverture est visible uniquement si possible (livré + < 4j + pas de litige)
  if (!dispute && !can_open) return null;

  const isParty = dispute && user && (user.id === dispute.shipper_id || user.id === dispute.transporter_id);
  const canRespond = dispute && isParty
    && user.id !== dispute.opened_by
    && (dispute.status === "open" || dispute.status === "responded");
  const hasRespondedAlready = dispute?.timeline?.some(
    (e) => e.action === "response" && e.by === user?.id
  );

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-md p-5 mt-6" data-testid="dispute-panel">
      {!dispute && can_open && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-1" />
            <div>
              <div className="font-display font-bold text-base">Un problème avec cette livraison ?</div>
              <div className="text-sm text-neutral-700 mt-1">
                Signalez l’incident sans attendre. Les fonds encore disponibles sont soumis à revue ; vos recours légaux restent distincts de ce signalement.
              </div>
            </div>
          </div>
          <Button
            onClick={() => { setDialogMode("open"); setShowDialog(true); }}
            variant="outline"
            className="rounded-full border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white gap-2"
            data-testid="open-dispute-btn"
          >
            <AlertTriangle className="w-4 h-4" /> Signaler un litige
          </Button>
        </div>
      )}

      {dispute && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              {(() => {
                const S = STATUS_LABELS[dispute.status] || STATUS_LABELS.open;
                const Icon = S.icon;
                return (
                  <span className={`text-[10px] font-mono px-2 py-1 rounded ${S.color} flex items-center gap-1`}>
                    <Icon className="w-3 h-3" /> {S.label}
                  </span>
                );
              })()}
              <span className="text-xs font-mono text-neutral-600">
                {TYPE_LABELS[dispute.type]} · ouvert le {formatDate(dispute.created_at)}
              </span>
            </div>
            {dispute.deadline_admin && dispute.status !== "resolved" && (
              <span className="text-xs text-neutral-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Délai admin : {formatDate(dispute.deadline_admin)}
              </span>
            )}
          </div>

          {dispute.status === "resolved" && dispute.decision && (
            <div className="mb-4 p-3 bg-white rounded-md border border-emerald-300">
              <div className="text-xs font-mono text-emerald-700 uppercase tracking-wide">Décision</div>
              <div className="font-semibold mt-1">{DECISION_LABELS[dispute.decision]}</div>
              {dispute.refund_amount > 0 && (
                <div className="text-sm text-neutral-700 mt-1">
                  Montant remboursé : <b>{dispute.refund_amount}€</b>
                </div>
              )}
              {dispute.admin_notes && (
                <div className="text-sm text-neutral-600 mt-2 italic">« {dispute.admin_notes} »</div>
              )}
            </div>
          )}

          {/* Timeline conversationnelle */}
          <div className="space-y-3">
            {(dispute.timeline || []).map((e, i) => (
              <div key={`${e.at}-${e.side}-${i}`} className="bg-white rounded-md border border-neutral-200 p-3" data-testid={`dispute-entry-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-3 h-3 text-neutral-400" />
                  <span className="text-xs font-mono uppercase tracking-wide text-neutral-600">
                    {e.side === "shipper" ? "Expéditeur" : e.side === "transporter" ? "Transporteur" : e.side}
                  </span>
                  <span className="text-xs text-neutral-400">· {formatDate(e.at)}</span>
                </div>
                <div className="text-sm text-neutral-800 whitespace-pre-wrap">{e.message}</div>
                {(e.photos || []).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {e.photos.map((p, j) => (
                      <a key={`${p.slice(-32)}-${j}`} href={p} target="_blank" rel="noopener noreferrer" className="block w-20 h-20 rounded-md overflow-hidden border border-neutral-200 hover:border-[#1F4032]">
                        <img src={p} alt={`document ${j + 1}`} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {canRespond && !hasRespondedAlready && (
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => { setDialogMode("respond"); setShowDialog(true); }}
                className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white gap-2"
                data-testid="respond-dispute-btn"
              >
                <MessageSquare className="w-4 h-4" /> Répondre au litige
              </Button>
            </div>
          )}
        </>
      )}

      <DisputeDialog
        open={showDialog}
        mission={mission}
        mode={dialogMode}
        dispute={dispute}
        onClose={() => setShowDialog(false)}
        onDone={() => { load(); onChange?.(); }}
      />
    </div>
  );
}
